import { AuthorGate, authorByIdQuery } from '@/entities/authors'
import { ROUTES } from '@/shared/config'
import { useHashTabs } from '@/shared/lib/react-hooks'
import {
  ContentWrapper,
  ErrorResult,
  HeaderContent,
  HidingSpin,
} from '@/shared/ui'
import { AuthorForm } from '@/widgets/AuthorForm'
import { Breadcrumb, Layout, Tabs } from 'antd'
import { useUnit } from 'effector-react'
import { Link, useParams } from 'react-router-dom'

import {
  type AuthorEditPageTabsKeys,
  AuthorEditPageTabsKeysArray,
  AuthorEditPageTabsKeysEnum,
} from './lib/tabs'
import {
  $authorEditPageTab,
  AuthorEditPageGate,
  authorEditPageTabChanged,
} from './model/author-edit.model'
import { AuthorAlbumCollectionsTab } from './ui/tabs/AuthorAlbumCollectionsTab'
import { AuthorAlbumsTab } from './ui/tabs/AuthorAlbumsTab'
import { AuthorAudioBooksTab } from './ui/tabs/AuthorAudioBooksTab'
import { AuthorMusicTab } from './ui/tabs/AuthorMusicTab'
import { AuthorPodcastsTab } from './ui/tabs/AuthorPodcastsTab'
import { AuthorSermonsTab } from './ui/tabs/AuthorSermonsTab'
import { AuthorSettingsTab } from './ui/tabs/AuthorSettingsTab'

export const AuthorEditPage = () => {
  const { id } = useParams<{ id: string }>()
  const { data, pending, error } = useUnit(authorByIdQuery)

  const { tab, handleTabChange } = useHashTabs<AuthorEditPageTabsKeys>(
    AuthorEditPageTabsKeysArray,
    $authorEditPageTab,
    authorEditPageTabChanged,
  )

  return (
    <>
      <AuthorEditPageGate />
      {id && <AuthorGate id={id} />}
      <HidingSpin spinning={pending || (!data && !error)}>
        <Layout style={{ minHeight: '50vh' }}>
          {data && !error ? (
            <>
              <HeaderContent
                breadcrumbSlot={
                  <Breadcrumb
                    items={[
                      {
                        title: <Link to={ROUTES.AUTHORS.ROOT}>Авторы</Link>,
                      },
                      { title: data.nickname },
                    ]}
                  />
                }
                title={data.nickname}
                description="Раздел позволяет редактировать информацию об авторе"
              />
              <ContentWrapper verticalPadding="small">
                <Tabs
                  destroyOnHidden
                  activeKey={tab}
                  onChange={handleTabChange}
                  tabPosition="left"
                  items={[
                    {
                      key: AuthorEditPageTabsKeysEnum.common,
                      label: 'Общая информация',
                      children: <AuthorForm />,
                    },
                    {
                      key: AuthorEditPageTabsKeysEnum.music,
                      label: 'Музыка',
                      children: (
                        <AuthorMusicTab
                          author={{ id: data.id, nickname: data.nickname }}
                        />
                      ),
                    },
                    {
                      key: AuthorEditPageTabsKeysEnum.podcast,
                      label: 'Подкасты',
                      children: (
                        <AuthorPodcastsTab
                          author={{ id: data.id, nickname: data.nickname }}
                        />
                      ),
                    },
                    {
                      key: AuthorEditPageTabsKeysEnum.sermon,
                      label: 'Проповеди',
                      children: (
                        <AuthorSermonsTab
                          author={{ id: data.id, nickname: data.nickname }}
                        />
                      ),
                    },
                    {
                      key: AuthorEditPageTabsKeysEnum.audiobook,
                      label: 'Аудиокниги',
                      children: (
                        <AuthorAudioBooksTab
                          author={{ id: data.id, nickname: data.nickname }}
                        />
                      ),
                    },
                    {
                      key: AuthorEditPageTabsKeysEnum.album,
                      label: 'Альбомы',
                      children: (
                        <AuthorAlbumsTab
                          author={{ id: data.id, nickname: data.nickname }}
                        />
                      ),
                    },
                    {
                      key: AuthorEditPageTabsKeysEnum.albumcollection,
                      label: 'Выпуски',
                      children: (
                        <AuthorAlbumCollectionsTab
                          author={{ id: data.id, nickname: data.nickname }}
                        />
                      ),
                    },
                    {
                      key: AuthorEditPageTabsKeysEnum.settings,
                      label: 'Настройки',
                      children: <AuthorSettingsTab author={data} />,
                    },
                  ]}
                />
              </ContentWrapper>
            </>
          ) : (
            <ContentWrapper>
              <ErrorResult
                error={error}
                notFoundConfig={{
                  subTitle: 'Данного исполнителя не существует.',
                  navigateTo: ROUTES.AUTHORS.ROOT,
                }}
              />
            </ContentWrapper>
          )}
        </Layout>
      </HidingSpin>
    </>
  )
}
